import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { formatTime } from '@brimo/shared';
import { Button, Dialog } from '../../../ds/components';
import { formatPhone } from '../../settings/connections';
import { chatBackfillLine } from '../../settings/WhatsAppSettingsPage';
import { useStepAction, useUserChecklist, userChecklistKey } from './api';
import { PairingPanel, useWhatsappPairing } from './WhatsAppPairing';
import styles from './WhatsAppDialog.module.css';

/** „nur noch das Zahlungsmittel" — the one step left, with its article. */
const REMAINING: Record<string, string> = {
  profile: 'das Profil',
  mailbox: 'das Postfach',
  calendar: 'der Kalender',
  availability: 'die Verfügbarkeit',
  whatsapp: 'die WhatsApp-Session',
  signature: 'die Signatur',
  billing: 'das Zahlungsmittel',
  security: 'die Zwei-Faktor-Anmeldung',
};

/**
 * Onboarding Nutzer 14: the WhatsApp pairing from the checklist in a dialog; once the session is connected the step
 * is completed (`POST /setup/user/steps/whatsapp`) and the „Danach · Session aktiv" state follows.
 */
export function WhatsAppDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? <WhatsAppDialogContent onClose={onClose} /> : null;
}

function WhatsAppDialogContent({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pairing = useWhatsappPairing(true);
  const step = useStepAction();
  const checklist = useUserChecklist();
  const completed = useRef(false);
  const connected = pairing.state === 'connected';

  useEffect(() => {
    if (!connected || completed.current) return;
    completed.current = true;
    step.mutate(
      { key: 'whatsapp', action: 'continue' },
      { onSettled: () => void queryClient.invalidateQueries({ queryKey: userChecklistKey }) },
    );
  }, [connected]);

  const status = pairing.status;
  if (connected && status) {
    const progress = checklist.data?.progress;
    const remaining = (checklist.data?.open ?? []).filter((card) => card.key !== 'whatsapp');
    const billingOpen = remaining.some((card) => card.key === 'billing');
    const chats = chatBackfillLine(status);
    const summary = progress
      ? remaining.length === 0
        ? `Einrichtung ${progress.done} von ${progress.total}`
        : remaining.length === 1 && remaining[0]
          ? `Einrichtung ${progress.done} von ${progress.total} · nur noch ${REMAINING[remaining[0].key] ?? remaining[0].title}`
          : `Einrichtung ${progress.done} von ${progress.total} · noch ${remaining.length} Schritte offen`
      : null;
    return (
      <Dialog
        open
        onClose={onClose}
        eyebrow="Danach · Session aktiv"
        title="WhatsApp verbunden"
        width={420}
        footer={
          billingOpen ? (
            <Button onClick={() => void navigate('/settings/billing')}>Weiter zur Abrechnung</Button>
          ) : (
            <Button onClick={onClose}>Schließen</Button>
          )
        }
      >
        <div className={styles.body}>
          <div className={styles.connected}>
            <span className={styles.mark} aria-hidden="true">
              ✓
            </span>
            <span>
              <span className={styles.title}>Verbunden</span>
              <span className={styles.sub}>
                {[status.phoneE164 ? formatPhone(status.phoneE164) : null, status.deviceName]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
          </div>
          <dl className={styles.facts}>
            {chats ? (
              <div className={styles.fact}>
                <dt>Zuordnung</dt>
                <dd>{chats}</dd>
              </div>
            ) : null}
            {status.connectedSince ? (
              <div className={styles.fact}>
                <dt>Verbunden seit</dt>
                <dd>{formatTime(status.connectedSince)}</dd>
              </div>
            ) : null}
          </dl>
          {summary ? (
            <div className={styles.summary} role="status">
              <span aria-hidden="true">✓</span>
              {summary}
            </div>
          ) : null}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Aus der Checkliste"
      title="WhatsApp verbinden"
      width={420}
      footer={
        <>
          <Button variant="secondary" loading={pairing.busy} disabled={!status} onClick={pairing.refresh}>
            Neuer Code
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Später
          </Button>
        </>
      }
    >
      <PairingPanel pairing={pairing} size={212} variant="dialog" />
    </Dialog>
  );
}
